/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Chaitanya
*/

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { View, StyleSheet, TouchableOpacity, Dimensions } from 'react-native';
import Toast from 'react-native-toast-message';
import { FontAwesome } from '@expo/vector-icons';

import SociollaText from '@components/sociolla/SociollaText';
import ItemDetail from '@components/global/ItemDetail';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import * as Layout from '@components/screen/Layout';
import StatusBadge from '@components/global/StatusBadge';
import AttachmentsInput from '@components/form/AttachmentsInput';
import Image from '@components/global/Image';
import SelectBox from '@components/form/SelectBox';
import RenderIf from '@components/global/RenderIf';
import ScrollContainer from '@components/global/ScrollContainer';
import ModalLog from '@components/modal/ModalLog';
import FloatCommentButton from '@components/comment/FloatCommentButton';
import CommentHeaderButton from '@components/comment/CommentHeaderButton';
import CommentDrawer from '@components/comment/CommentDrawer';
import MoreButton from '@components/navbar/MoreButton';
import NotificationSettingButton from '@components/navbar/NotificationSettingButton';

import { isWeb, isNative } from '@global/screens';
import {
  hasNoAccess,
  parseDate,
  currency,
  formatCoaTaxLabel,
  formatSkpType,
  getHeaderTitleStyle,
} from '@global/helpers';
import useApi from '@/hooks/useApi';
import { MsOrder } from '@api/ms-order';
import { showAlert, setTempData, setSkpGenerateStatus } from '@/store/global';
import { showPopup } from '@/store/modal';
import { SKP_INTERNAL_LOG_FORMATTER } from '@/constants/log';

const img = require('@/assets/img/default/user-image.png');

const SkpInternalDetailScreen = (props) => {
  const dispatch = useDispatch();
  const userRole = useSelector((state) => state.auth?.user?.role?.code);

  const getSkpInternalDetail = useApi(MsOrder.getSkpInternalDetail);
  const getSkpInternalsBrandBudgetCount = useApi(
    MsOrder.getSkpInternalsBrandBudgetCount,
  );
  const getCoaTaxSettingsDetail = useApi(MsOrder.getCoaTaxSettingsDetail);
  const getSkpExternalCount = useApi(MsOrder.getSkpExternalCount);

  const [detailData, setDetailData] = useState({});
  const [showComments, setShowComments] = useState(
    props.route.params?.openComments === 'true',
  );
  const [customerBrandBudgetTableCount, setCustomerBrandBudgetTableCount] =
    useState(0);
  const [skpExternalCount, setSkpExternalCount] = useState(0);
  const [showModalLog, setShowModalLog] = useState(false);

  const showEditButton = !hasNoAccess([
    'superadmin',
    'general_manager_marketing',
    'marketing_admin',
    'general',
  ]);

  const showGenerateSkpExternalButton = !hasNoAccess([
    'superadmin',
    'regional_manager',
    'regional_support',
    'key_account_manager',
    'group_key_account_manager',
    'national_key_account_manager',
    'general_manager_marketing',
    'general',
  ]);
  const canApprove = () => {
    if (detailData?.status == 'need_approval' && detailData?.current_approval) {
      return Boolean(detailData?.current_approval == userRole);
    } else {
      return false;
    }
  };

  const User = (userProps) => {
    return (
      <View
        style={{
          flex: 1,
          flexDirection: 'row',
          justifyContent: 'flex-start',
          ...userProps.style,
        }}
        key={'list-users-' + userProps.index}>
        <Image
          style={{ width: 20, height: 20, marginRight: 8 }}
          width={20}
          height={20}
          isResolveAsset
          source={userProps.image ? userProps.image : img}
        />
        <SociollaText
          style={{ fontSize: 12, color: Colors.blue3, alignSelf: 'center' }}>
          {userProps.name}
        </SociollaText>
      </View>
    );
  };

  const onChangeActive = async (isActive) => {
    try {
      await MsOrder.updateStatusActiveSkpInternal(detailData?._id, {
        is_active: isActive,
      });
    } catch (error) {}
  };

  const getDetail = async (id) => {
    const response = await getSkpInternalDetail.request(id);
    const customerBrandBudgetCount =
      await getSkpInternalsBrandBudgetCount.request({
        params: { filter: { 'skp_internal.id': id } },
      });
    if (response?.data?.data && Object.keys(response.data.data).length) {
      const result = response.data.data;
      result.id = result._id;
      if (customerBrandBudgetCount?.data?.data) {
        setCustomerBrandBudgetTableCount(customerBrandBudgetCount.data.data);
      }
      dispatch(setTempData(result.status));
      dispatch(setSkpGenerateStatus(result.is_generate_skp));

      // Handle old skp data
      if (
        !result.coa_tax.tax_corporate ||
        !result.coa_tax.tax_personal ||
        !result.coa_tax.non_tax_corporate ||
        !result.coa_tax.non_tax_personal
      ) {
        const coaTax = await getCoaTaxSettingsDetail.request(result.coa_tax.id);
        if (coaTax?.data?.data && Object.keys(coaTax.data.data).length) {
          const coaTaxData = coaTax.data.data;
          result.coa_tax = {
            ...result.coa_tax,
            tax_corporate: (coaTaxData.tax_corporate || []).find(
              (t) => t.is_default,
            ),
            tax_personal: (coaTaxData.tax_personal || []).find(
              (t) => t.is_default,
            ),
            non_tax_corporate: (coaTaxData.non_tax_corporate || []).find(
              (t) => t.is_default,
            ),
            non_tax_personal: (coaTaxData.non_tax_personal || []).find(
              (t) => t.is_default,
            ),
          };
        }
      }

      const mt = result.market_type?.code || '';
      result.skp_type = result.skp_type || mt.toLowerCase();

      const skpExternal = await getSkpExternalCount.request({
        params: {
          filter: {
            'skp_internal.id': id,
          },
        },
      });
      setSkpExternalCount(skpExternal?.data?.data || 0);

      setDetailData(result);
    }
  };

  const changeStatus = async (id, status) => {
    try {
      if (id) {
        dispatch(showPopup({ name: 'isLoading', value: true }));
        const payload = {};
        const requestConfirm = await MsOrder.changeStatusSkpInternal(
          id,
          status,
          payload,
        );
        if (requestConfirm && requestConfirm.data && requestConfirm.data.data) {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          let title = '';
          let desc = '';
          if (status == 'need_approval') {
            title = 'SKP Internal Submitted';
            desc = 'SKP Internal submitted and need confirmation';
          } else if (status == 'cancelled') {
            title = 'SKP Internal Cancelled';
            desc = 'SKP Internal has been cancelled';
          } else if (status == 'approved') {
            title = 'SKP Internal Confirmed';
            desc = 'SKP Internal has been confirmed';
          } else if (status == 'draft') {
            title = 'SKP Internal Submitted';
            desc = 'SKP Internal has been drafted';
          } else if (status == 'rejected') {
            title = 'SKP Internal Rejected';
            desc = 'SKP Internal has been Rejected';
          }
          dispatch(
            showAlert({
              show: true,
              title: title,
              description: desc,
              isConfirm: false,
              onCallback: () => {
                getDetail(props.route.params.id);
              },
            }),
          );
        } else {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({ text1: 'A system error occurred', type: 'error' });
        }
      }
    } catch (error) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      dispatch(
        showAlert({
          show: true,
          title: 'Error',
          description:
            error?.response?.data?.message || 'A system error occurred',
          isConfirm: false,
        }),
      );
    }
  };

  const removeSkp = async (id) => {
    dispatch(
      showAlert({
        show: true,
        title: 'Delete SKP Internal',
        description:
          'This SKP Internal will be deleted. Do you want to proceed?',
        onCallback: async () => {
          try {
            if (id) {
              dispatch(showPopup({ name: 'isLoading', value: true }));
              const requestDelete = await MsOrder.deleteSkpInternal(id);
              if (requestDelete?.status == 200) {
                dispatch(showPopup({ name: 'isLoading', value: false }));
                props.navigation.navigate('SkpInternalScreen');
              } else {
                dispatch(showPopup({ name: 'isLoading', value: false }));
                Toast.show({
                  text1: 'A system error occurred',
                  type: 'error',
                });
              }
            }
          } catch (error) {
            dispatch(showPopup({ name: 'isLoading', value: false }));
            Toast.show({ text1: 'A system error occurred', type: 'error' });
          }
        },
      }),
    );
  };

  const createSkpExternals = async (id) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    dispatch(
      showAlert({
        show: true,
        title: 'Generate Bulk SKP External',
        description:
          'You can generate bulk SKP external only once. Do you want to proceed?',
        onCallback: async () => {
          try {
            const response = await MsOrder.generateSkpExternals(id);
            if (response.data?.success) {
              dispatch(showPopup({ name: 'isLoading', value: false }));
              Toast.show({
                text1: 'SKP Externals has been generated',
                type: 'success',
              });
            } else {
              dispatch(showPopup({ name: 'isLoading', value: false }));
              Toast.show({
                text1: 'Unable to generate SKP Externals',
                type: 'error',
              });
            }
          } catch (error) {
            dispatch(showPopup({ name: 'isLoading', value: false }));
            Toast.show({
              text1:
                error?.response?.data?.message ||
                'Unable to generate SKP Externals',
              type: 'error',
            });
          } finally {
            getDetail(props.route.params.id);
          }
        },
        onCallbackCancel: () => {
          dispatch(showPopup({ name: 'isLoading', value: false }));
        },
      }),
    );
  };

  const handleGoToSkpExternal = () => {
    if (detailData?.reference_code) {
      props.navigation.navigate('SkpExternalListScreen', {
        id: detailData._id,
      });
    }
  };

  const from = props?.route?.params?.name;
  const goBack = () => {
    if (from === 'SKP Internal Approval') {
      props.navigation.navigate('TaskNotificationNavigator', {
        screen: 'TaskNotificationScreen',
        params: {
          task: true,
        },
      });
    } else {
      props.navigation.replace('SkpInternalDetailScreen', {});
      props.navigation.navigate('SkpInternalScreen');
    }
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      dispatch(setTempData(null));
      setDetailData({});
      if (props?.route?.params?.id) {
        getDetail(props.route.params.id);
      }
    }
    return () => {
      mounted = false;
    };
  }, [props]);

  useEffect(() => {
    const menus = [];
    if (
      showEditButton &&
      detailData?.status != 'need_approval' &&
      detailData?.status != 'archived'
    ) {
      menus.push({
        type: 'function',
        name: 'Edit',
        callback: () =>
          props.navigation.replace('SkpInternalFormScreen', {
            id: detailData._id,
            name: detailData.name ? detailData.reference_code : '-',
            titleScreen: detailData.reference_code
              ? detailData.reference_code
              : '-',
          }),
      });
    }
    if (showEditButton && detailData?.status == 'draft') {
      menus.push({
        type: 'function',
        name: 'Delete',
        callback: () => removeSkp(detailData?._id),
      });
    }
    if (
      showGenerateSkpExternalButton &&
      detailData?.status == 'approved' &&
      !detailData?.is_generate_skp
    ) {
      menus.push({
        type: 'function',
        name: 'Generate SKP Externals',
        callback: () => createSkpExternals(detailData?._id),
      });
    }

    const windowWidth = Dimensions.get('window').width;
    props.navigation.setOptions({
      // [B2B-5891][B2B-5924] This screen's native headerRight renders TWO buttons
      // (Comment + More, ~50px each), but the default title maxWidth (50% of the screen)
      // assumes one, so long names overlap the Comment button on narrow screens (e.g.
      // mobile web). Size the title to the space actually left in the row: Back 50 +
      // title margins 16/16 + Comment 50 + More 50 = 182px. Only applies when the Comment
      // button is shown (once detailData._id is available); otherwise headerRight is just
      // the single MoreButton, which the default already covers.
      headerTitleStyle:
        !isWeb && detailData._id
          ? getHeaderTitleStyle(windowWidth - 182)
          : undefined,
      headerRight: () =>
        isWeb ? (
          <NotificationSettingButton {...props} />
        ) : (
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            {detailData._id ? (
              <CommentHeaderButton
                onPress={() => setShowComments(true)}
                drawerVisible={showComments}
                entityType="skp_internal"
                entityId={detailData._id}
              />
            ) : (
              false
            )}
            <MoreButton menu={menus} {...props} />
          </View>
        ),
    });
  }, [
    props,
    detailData,
    showComments,
    showEditButton,
    showGenerateSkpExternalButton,
  ]);

  return (
    <Layout.Container testID="skp-internal-detail-screen">
      {getSkpInternalDetail?.loading ? (
        <Layout.Wrapper>
          <Layout.Loader />
        </Layout.Wrapper>
      ) : detailData && Object.keys(detailData).length ? (
        <>
          {isWeb ? (
            <Layout.Wrapper white={false}>
              <Layout.Back
                testID="skp-internal-detail-back"
                title={
                  from === 'SKP Internal Approval' ? 'Task' : 'Skp Internal'
                }
                onCallback={() => goBack()}
              />
            </Layout.Wrapper>
          ) : (
            false
          )}
          <ScrollContainer>
            <Layout.Wrapper rounded>
              <View style={styles.sectionWrapper}>
                {isWeb ? (
                  <View
                    style={[
                      styles.rowContainer,
                      {
                        position: 'relative',
                        zIndex: 1,
                        justifyContent: 'space-between',
                      },
                    ]}>
                    <SociollaText
                      testID="skp-internal-detail-title"
                      type="medium"
                      style={{ fontSize: 20 }}>
                      {detailData?.name || ''}
                    </SociollaText>
                    {showEditButton || showGenerateSkpExternalButton ? (
                      <View style={{ flexDirection: 'row' }}>
                        {showGenerateSkpExternalButton &&
                        detailData?.status == 'approved' &&
                        !detailData?.is_generate_skp ? (
                          <Button
                            testID="skp-internal-detail-generate-external-btn"
                            type="primary"
                            rounded
                            buttonStyle={{
                              width: 185,
                              marginRight: 10,
                              backgroundColor: Colors.blue0,
                            }}
                            title={
                              <SociollaText
                                type="bold"
                                style={{ color: Colors.white }}>
                                Generate Skp External
                              </SociollaText>
                            }
                            onPress={() => createSkpExternals(detailData?._id)}
                          />
                        ) : (
                          false
                        )}
                        {showEditButton && detailData?.status == 'draft' ? (
                          <Button
                            testID="skp-internal-detail-request-confirm-btn"
                            type="primary"
                            rounded
                            disabled={!customerBrandBudgetTableCount}
                            disabledStyle={{
                              width: 185,
                              marginRight: 10,
                            }}
                            buttonStyle={{
                              width: 185,
                              marginRight: 10,
                              backgroundColor: Colors.blue0,
                            }}
                            title={
                              <SociollaText
                                type="bold"
                                style={{ color: Colors.white }}>
                                Request Confirmation
                              </SociollaText>
                            }
                            onPress={() => {
                              if (!detailData?.attachment?.length) {
                                Toast.show({
                                  text1: 'Attachment is required',
                                  type: 'error',
                                });
                                return;
                              }
                              changeStatus(detailData?._id, 'need_approval');
                            }}
                          />
                        ) : (
                          false
                        )}
                        {showEditButton &&
                        detailData?.status != 'need_approval' &&
                        detailData?.status != 'archived' ? (
                          <Button
                            testID="skp-internal-detail-edit-btn"
                            type="primary"
                            rounded
                            buttonStyle={{ width: 80, marginRight: 10 }}
                            title={
                              <SociollaText
                                type="bold"
                                style={{ color: Colors.white }}>
                                Edit
                              </SociollaText>
                            }
                            onPress={() =>
                              props.navigation.replace(
                                'SkpInternalFormScreen',
                                {
                                  id: detailData._id,
                                  name: detailData.name
                                    ? detailData.reference_code
                                    : '-',
                                  titleScreen: detailData.reference_code
                                    ? detailData.reference_code
                                    : '-',
                                },
                              )
                            }
                          />
                        ) : (
                          false
                        )}
                        {showEditButton && detailData?.status == 'draft' ? (
                          <Button
                            testID="skp-internal-detail-delete-btn"
                            type="primary"
                            rounded
                            buttonStyle={{
                              width: 42,
                              marginRight: 10,
                              backgroundColor: Colors.blue1,
                            }}
                            title={
                              <Image
                                style={{
                                  width: 13,
                                  height: 17,
                                }}
                                width={13}
                                height={17}
                                source={require('@assets/img/icons/trash-white.svg')}
                                svg={true}
                              />
                            }
                            onPress={() => removeSkp(detailData?._id)}
                          />
                        ) : (
                          false
                        )}
                      </View>
                    ) : (
                      false
                    )}
                    {canApprove() ? (
                      <View style={{ flexDirection: 'row' }}>
                        <Button
                          testID="skp-internal-detail-reject-btn"
                          type="primary"
                          rounded
                          buttonStyle={{
                            width: 150,
                            marginRight: 10,
                            backgroundColor: Colors.white,
                          }}
                          title={
                            <SociollaText type="bold" style={{}}>
                              Reject
                            </SociollaText>
                          }
                          onPress={() =>
                            changeStatus(detailData?._id, 'rejected')
                          }
                        />
                        <Button
                          testID="skp-internal-detail-approve-btn"
                          type="primary"
                          rounded
                          buttonStyle={{
                            width: 150,
                            marginRight: 10,
                            backgroundColor: Colors.blue0,
                          }}
                          title={
                            <SociollaText
                              type="bold"
                              style={{ color: Colors.white }}>
                              Approve
                            </SociollaText>
                          }
                          onPress={() =>
                            changeStatus(detailData?._id, 'approved')
                          }
                        />
                      </View>
                    ) : (
                      false
                    )}
                  </View>
                ) : (
                  false
                )}
                <View
                  style={[
                    { maxWidth: isWeb ? 400 : '100%' },
                    isWeb ? { paddingVertical: 15 } : { paddingBottom: 20 },
                  ]}>
                  <ItemDetail
                    testID="skp-internal-detail-skp-number"
                    name="SKP Number"
                    value={
                      detailData?.reference_code
                        ? detailData.reference_code
                        : '-'
                    }
                  />
                  <ItemDetail
                    testID="skp-internal-detail-status"
                    name="Status"
                    centered
                    valueComponent={
                      <View
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                        }}>
                        <StatusBadge status={detailData.status} />
                        <TouchableOpacity
                          testID="skp-internal-detail-see-log-btn"
                          onPress={() => setShowModalLog(true)}>
                          <SociollaText
                            style={{
                              fontSize: 12,
                              color: Colors.blue0,
                              marginLeft: 20,
                              zIndex: 99,
                            }}>
                            see log
                          </SociollaText>
                        </TouchableOpacity>
                      </View>
                    }
                  />
                  {detailData?.status == 'need_approval' && (
                    <ItemDetail
                      testID="skp-internal-detail-waiting-approval"
                      name="Waiting Approval"
                      withoutBorder={true}
                      valueComponent={
                        <View style={{ flexDirection: 'column' }}>
                          {detailData.waiting_approvals?.length
                            ? detailData.waiting_approvals.map(
                                (appr, index) => (
                                  <User
                                    name={appr.name || ''}
                                    image={appr.image || ''}
                                    key={'list-user-approval-' + index}
                                    index={index}
                                    style={{ paddingBottom: 10 }}
                                  />
                                ),
                              )
                            : false}
                        </View>
                      }
                    />
                  )}
                  <ItemDetail
                    testID="skp-internal-detail-promo-external"
                    name="Promotion Agreement External"
                    centered
                    valueComponent={
                      <View
                        style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <SociollaText
                          style={{
                            fontSize: !isNative ? 12 : 13,
                            marginLeft: 20,
                            marginRight: 20,
                          }}>
                          {skpExternalCount}
                        </SociollaText>
                        {skpExternalCount > 0 && (
                          <Button
                            testID="skp-internal-detail-see-all-external-btn"
                            type="primary"
                            rounded={true}
                            buttonStyle={{
                              width: 100,
                              backgroundColor: Colors.white,
                            }}
                            title={
                              <View
                                style={{
                                  flexDirection: 'row',
                                  alignItems: 'center',
                                }}>
                                <FontAwesome
                                  name="eye"
                                  size={12}
                                  color={Colors.black}
                                  style={{ marginRight: 8 }}
                                />

                                <SociollaText
                                  type="medium"
                                  style={{ fontSize: 14, color: Colors.black }}>
                                  See All
                                </SociollaText>
                              </View>
                            }
                            onPress={handleGoToSkpExternal}
                          />
                        )}
                      </View>
                    }
                  />
                  <ItemDetail
                    testID="skp-internal-detail-description"
                    name="Description"
                    value={
                      detailData?.description ? detailData.description : '-'
                    }
                  />
                  <ItemDetail
                    testID="skp-internal-detail-coa"
                    name="COA"
                    centered
                    value={
                      detailData?.coa_tax?.cost_type
                        ? detailData.coa_tax.cost_type
                        : '-'
                    }
                  />
                  <ItemDetail
                    testID="skp-internal-detail-tax-corporate"
                    name="Tax ID Corporate"
                    centered
                    value={formatCoaTaxLabel(
                      detailData?.coa_tax?.tax_corporate,
                    )}
                  />
                  <ItemDetail
                    testID="skp-internal-detail-tax-personal"
                    name="Tax ID Personal"
                    centered
                    value={formatCoaTaxLabel(detailData?.coa_tax?.tax_personal)}
                  />
                  <ItemDetail
                    testID="skp-internal-detail-type"
                    name="Type"
                    centered
                    value={
                      detailData?.skp_type
                        ? formatSkpType(detailData.skp_type)
                        : '-'
                    }
                  />
                  <ItemDetail
                    testID="skp-internal-detail-periode"
                    name="Periode"
                    centered
                    value={
                      detailData.from_date || detailData.to_date
                        ? `${
                            detailData.from_date
                              ? parseDate(detailData.from_date)
                              : '-'
                          } - ${
                            detailData.to_date
                              ? parseDate(detailData.to_date)
                              : '-'
                          }`
                        : '-'
                    }
                  />
                  <ItemDetail
                    testID="skp-internal-detail-is-active"
                    name="Is Active"
                    centered
                    valueComponent={
                      !hasNoAccess([
                        'superadmin',
                        'general_manager_marketing',
                        'general',
                      ]) ? (
                        <SelectBox
                          testID="skp-internal-detail-active-select"
                          label="Status"
                          hideLabel
                          selectContainer={{ width: isWeb ? 'auto' : 200 }}
                          options={[
                            { name: 'Active', value: true },
                            { name: 'Inactive', value: false },
                          ]}
                          value={{
                            name: detailData.is_active ? 'Active' : 'Inactive',
                            value: detailData.is_active,
                          }}
                          trackBy="value"
                          labelKey="name"
                          onChange={(val) => {
                            const finalValue = val.value;
                            const payload = {
                              ...detailData,
                              is_active: finalValue,
                            };
                            setDetailData(payload);
                            onChangeActive(finalValue);
                          }}
                        />
                      ) : (
                        <StatusBadge
                          status={detailData.is_active ? 'active' : 'in_active'}
                        />
                      )
                    }
                  />
                  <ItemDetail
                    testID="skp-internal-detail-estimate-budget"
                    name="Total Estimate Budget"
                    centered
                    value={
                      detailData.total_estimate_budget
                        ? currency(detailData.total_estimate_budget, true)
                        : currency(0, true)
                    }
                  />
                  <ItemDetail
                    testID="skp-internal-detail-actual-budget"
                    name="Total Actual Budget"
                    centered
                    value={
                      detailData.total_actual_budget
                        ? currency(detailData.total_actual_budget, true)
                        : currency(0, true)
                    }
                  />
                  <ItemDetail
                    testID="skp-internal-detail-budget-used"
                    name="Budget Used"
                    centered
                    value={
                      detailData.budget_used
                        ? currency(detailData.budget_used, true)
                        : currency(0, true)
                    }
                  />
                  <ItemDetail
                    testID="skp-internal-detail-budget-remaining"
                    name="Total Budget Remaining"
                    centered
                    value={
                      detailData.budget_remaining
                        ? currency(detailData.budget_remaining, true)
                        : currency(0, true)
                    }
                  />
                  <ItemDetail
                    testID="skp-internal-detail-last-updated"
                    name="Last Updated, By"
                    centered
                    value={`${
                      detailData.updated_at
                        ? parseDate(detailData.updated_at)
                        : '-'
                    }${
                      detailData?.updated_by?.name
                        ? `, ${detailData.updated_by.name}`
                        : ''
                    }`}
                  />
                  <ItemDetail
                    testID="skp-internal-detail-customer-brand"
                    name="List Customer And Brand"
                    centered
                    value={
                      <>
                        <RenderIf condition={detailData?.is_csv_processing}>
                          <SociollaText>Processing...</SociollaText>
                        </RenderIf>
                        <RenderIf condition={!detailData?.is_csv_processing}>
                          {customerBrandBudgetTableCount ? (
                            <Button
                              testID="skp-internal-detail-view-budget-btn"
                              type="primary"
                              rounded={true}
                              buttonStyle={{
                                width: 100,
                                backgroundColor: Colors.white,
                                marginTop: 10,
                              }}
                              title={
                                <SociollaText
                                  type="medium"
                                  style={{ fontSize: 14, color: Colors.black }}>
                                  View
                                </SociollaText>
                              }
                              onPress={() =>
                                props.navigation.navigate(
                                  'SkpInternlBudgetTableScreen',
                                  { id: detailData._id, is_detail_view: true },
                                )
                              }
                            />
                          ) : (
                            '-'
                          )}
                        </RenderIf>
                      </>
                    }
                  />
                  <ItemDetail
                    testID="skp-internal-detail-notes"
                    name="Notes"
                    value={detailData?.notes ? detailData.notes : '-'}
                  />
                  <ItemDetail
                    testID="skp-internal-detail-attachment"
                    name="Attachment"
                    centered
                    value={
                      <AttachmentsInput
                        showLabel={false}
                        label="Upload Attachments"
                        value={detailData.attachment}
                        onChange={(updatedAttachments) =>
                          setDetailData((prevState) => ({
                            ...prevState,
                            attachment: updatedAttachments,
                          }))
                        }
                        id={detailData._id}
                        getDetail={getDetail}
                      />
                    }
                  />
                  {!isWeb && (
                    <View
                      style={{
                        height:
                          detailData?.attachment?.length > 1
                            ? 30 * detailData?.attachment?.length
                            : 250,
                      }}></View>
                  )}
                </View>
              </View>
            </Layout.Wrapper>
            {!isWeb && showEditButton && detailData?.status == 'draft' ? (
              <Layout.ButtonSticky
                testID="skp-internal-detail-sticky-confirm"
                route={props?.route}
                show={props?.route?.name == 'SkpInternalDetailScreen'}
                buttons={[
                  {
                    title: 'Request Confirmation',
                    disabled: !customerBrandBudgetTableCount,
                    onPress: () => {
                      if (!detailData?.attachment?.length) {
                        Toast.show({
                          text1: 'Attachment is required',
                          type: 'error',
                        });
                        return;
                      }
                      changeStatus(detailData?._id, 'need_approval');
                    },
                  },
                ]}
              />
            ) : (
              false
            )}
            {!isWeb && canApprove() ? (
              <Layout.ButtonSticky
                testID="skp-internal-detail-sticky-approve"
                route={props?.route}
                show={props?.route?.name == 'SkpInternalDetailScreen'}
                buttons={[
                  {
                    title: 'Reject',
                    type: 'primary',
                    onPress: () => {
                      changeStatus(detailData?._id, 'rejected');
                    },
                  },
                  {
                    title: 'Confirm',
                    onPress: () => {
                      changeStatus(detailData?._id, 'approved');
                    },
                  },
                ]}
              />
            ) : (
              false
            )}
          </ScrollContainer>
        </>
      ) : (
        false
      )}

      {showModalLog && (
        <ModalLog
          testID="skp-internal-detail-log-modal"
          show={showModalLog}
          status={detailData.status}
          onClose={() => setShowModalLog(false)}
          logType="skp_internal"
          logId={props.route.params.id}
          logLabel="SKP Internal"
          data={detailData}
          logFormatter={SKP_INTERNAL_LOG_FORMATTER}
        />
      )}
      {detailData._id ? (
        <>
          {isWeb ? (
            <FloatCommentButton
              onPress={() => setShowComments(true)}
              drawerVisible={showComments}
              entityType="skp_internal"
              entityId={detailData._id}
            />
          ) : (
            false
          )}
          <CommentDrawer
            visible={showComments}
            onClose={() => setShowComments(false)}
            entityType="skp_internal"
            entityId={detailData._id}
            entityRef={detailData.reference_code}
          />
        </>
      ) : (
        false
      )}
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  sectionWrapper: { width: '100%', padding: 15 },
  rowContainer: { flexDirection: 'row', alignItems: 'center' },
});

export default SkpInternalDetailScreen;
